import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import KPICards from './KPICards';
import CommonFunctions from './CommonFunctions';
import api from '../../api/api';

export default function Dashboard() {
  const navigate = useNavigate();
  const [cockpitLoading, setCockpitLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [invLoading, setInvLoading] = useState(true);
  const [alerts, setAlerts]         = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [productionReminders, setProductionReminders] = useState([]);
  const [prodRemLoading, setProdRemLoading] = useState(true);

  const [detailModal, setDetailModal] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const applyBootstrap = (data) => {
      if (cancelled) return;
      setKpis(data?.kpis ?? null);
      setInventory(Array.isArray(data?.inventory) ? data.inventory : []);
      setAlerts(Array.isArray(data?.alerts) ? data.alerts : []);
      setProductionReminders(Array.isArray(data?.productionReminders) ? data.productionReminders : []);
      setCockpitLoading(false);
      setInvLoading(false);
      setAlertsLoading(false);
      setProdRemLoading(false);
    };

    api.get('/dashboard/bootstrap')
      .then(applyBootstrap)
      .catch(() => {
        if (cancelled) return;
        setKpis(null);
        setInventory([]);
        setAlerts([]);
        setProductionReminders([]);
        setCockpitLoading(false);
        setInvLoading(false);
        setAlertsLoading(false);
        setProdRemLoading(false);
      });

    // Low-stock count scans 2.5M warehouse rows — refresh when background job finishes
    const lowStockTimer = setTimeout(() => {
      api.get('/dashboard/kpis')
        .then((kpis) => {
          if (cancelled || kpis?.lowStock == null) return;
          setKpis((prev) => (prev ? { ...prev, lowStock: kpis.lowStock } : kpis));
        })
        .catch(() => {});
    }, 8000);

    return () => {
      cancelled = true;
      clearTimeout(lowStockTimer);
    };
  }, []);

  const openAlertDetail = useCallback(async (alert) => {
    if (!alert.key) return;
    setDetailLoading(true);
    try {
      const qs = alert.extra?.po_id ? `?po_id=${alert.extra.po_id}` : '';
      const data = await api.get(`/dashboard/alert-detail/${alert.key}${qs}`);
      setDetailModal(data);
    } catch (e) {
      console.warn('Alert detail failed', e);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  return (
    <div className="space-y-4">
      {/* ── Page header ── */}
      <div className="flex items-center justify-between mb-2">
        <h1 className="page-title">Cockpit — ARM Welders MRP</h1>
        <span className="text-sm text-sap-textLight font-medium">
          {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </span>
      </div>

      {/* ── KPI cards ── */}
      <KPICards data={kpis} loading={cockpitLoading} />

      {/* ── Notifications: production orders due within 5 days (and overdue) ── */}
      <div className="card">
        <div className="card-header">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />
          </svg>
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-bold text-sap-text">Production due within 5 days (live)</span>
            <span className="text-[10px] text-sap-textLight font-normal mt-0.5">
              Latest activity in MRP first, then due date
              {prodRemLoading ? ' · …' : ` · ${productionReminders.length} order${productionReminders.length !== 1 ? 's' : ''}`}
            </span>
          </div>
        </div>
        <div className="p-3">
          {prodRemLoading ? (
            <p className="text-xs text-sap-textLight text-center py-3">Loading…</p>
          ) : productionReminders.length === 0 ? (
            <p className="text-xs text-sap-textLight text-center py-4">No active production orders due in the next 5 days or overdue.</p>
          ) : (
            <ul className="divide-y divide-sap-border/50 max-h-72 overflow-y-auto">
              {productionReminders.map((r, i) => {
                const days = Number(r.days_until);
                const overdue = days < 0;
                const dueLabel = overdue
                  ? `${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''} overdue`
                  : days === 0
                    ? 'Due today'
                    : `Due in ${days} day${days !== 1 ? 's' : ''}`;
                const code = (r.project_code && String(r.project_code).trim()) || '';
                const name = (r.project_name && String(r.project_name).trim()) || '';
                return (
                  <li key={r.prod_order_id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/production-order/${r.prod_order_id}`)}
                      className="w-full text-left py-2.5 px-1 rounded-lg hover:bg-sap-blue/5 transition-colors flex flex-col gap-1"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="text-xs font-semibold text-sap-text truncate">{r.order_no}</span>
                          {i === 0 && (
                            <span className="text-[9px] font-bold uppercase tracking-wide shrink-0 px-1 py-0 rounded bg-sap-blue/15 text-sap-blue">
                              Latest activity
                            </span>
                          )}
                        </div>
                        <span
                          className={`text-2xs font-bold shrink-0 px-1.5 py-0.5 rounded ${
                            overdue ? 'bg-red-100 text-red-800' : days <= 2 ? 'bg-amber-100 text-amber-900' : 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {dueLabel}
                        </span>
                      </div>
                      <div className="text-[10px] text-sap-text leading-snug space-y-0.5">
                        <div>
                          <span className="text-sap-textLight">Project code: </span>
                          <span className="font-medium">{code || '—'}</span>
                        </div>
                        <div>
                          <span className="text-sap-textLight">Project name: </span>
                          <span className="font-medium">{name || '—'}</span>
                        </div>
                      </div>
                      <span className="text-2xs text-sap-textLight">
                        Due {formatDate(r.due_date)} (from order master)
                        {r.status ? ` · ${r.status}` : ''}
                        {r.updated_at ? ` · MRP saved ${formatDateTime(r.updated_at)}` : ''}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* ── Two-column: Common Functions + Activity feed ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          <CommonFunctions />
        </div>

        {/* ── Messages / Alerts panel ── */}
        <div className="card">
          <div className="card-header">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />
            </svg>
            <span className="text-sm font-semibold text-sap-text">Alerts</span>
          </div>
          <div className="p-3 space-y-2">
            {alertsLoading ? (
              <p className="text-xs text-sap-textLight text-center py-3">Loading…</p>
            ) : alerts.length === 0 ? (
              <div className="flex flex-col items-center py-6 text-sap-textLight">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" className="mb-2 opacity-40">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="22 4 12 14.01 9 11.01" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p className="text-xs">All clear — no alerts</p>
              </div>
            ) : alerts.map((a, i) => (
              <AlertRow key={i} alert={a} time={formatAlertTime(a.time)} onClick={() => openAlertDetail(a)} loading={detailLoading} />
            ))}
          </div>
        </div>
      </div>

      {/* ── Alert Detail Modal ── */}
      {detailModal && (
        <AlertDetailModal
          data={detailModal}
          onClose={() => setDetailModal(null)}
          navigate={navigate}
        />
      )}

      {/* ── Quick statistics table ── */}
      <div className="card">
        <div className="card-header">
          <span className="text-sm font-semibold text-sap-text">Inventory Overview — Top Items</span>
        </div>
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-sap-blue/5 text-sap-textLight">
              <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Item No.</th>
              <th className="text-left px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Description</th>
              <th className="text-right px-3 py-2 font-semibold text-2xs uppercase tracking-wider">In Stock</th>
              <th className="text-right px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Committed</th>
              <th className="text-right px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Ordered</th>
              <th className="text-right px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Available</th>
              <th className="text-center px-3 py-2 font-semibold text-2xs uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody>
            {invLoading ? (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-sap-textLight">
                  Loading…
                </td>
              </tr>
            ) : inventory.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-sap-textLight">
                  No items found.
                </td>
              </tr>
            ) : (
              inventory.map((item) => {
                const isLow = Number(item.in_stock) < Number(item.min_order_qty || 0);
                return (
                  <StockRow
                    key={item.item_no}
                    no={item.item_no}
                    desc={item.description}
                    inStock={item.in_stock}
                    committed={item.committed}
                    ordered={item.ordered}
                    available={item.available}
                    status={isLow ? 'low' : 'ok'}
                  />
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── sub-components ── */

function formatAlertTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 2) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hr ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return d.toLocaleDateString('en-IN', { weekday: 'short' });
  return d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
}

function AlertRow({ alert, time, onClick, loading }) {
  const { type, text } = alert;
  const colors = {
    info:    'bg-blue-50   text-blue-700 hover:bg-blue-100',
    success: 'bg-green-50  text-green-700 hover:bg-green-100',
    warning: 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100',
    danger:  'bg-red-50    text-red-700 hover:bg-red-100',
  };
  const dots = { info: 'bg-blue-500', success: 'bg-green-500', warning: 'bg-yellow-500', danger: 'bg-red-500' };

  return (
    <button
      onClick={onClick}
      disabled={loading}
      className={`w-full flex items-start gap-2 px-2.5 py-2 rounded-xl text-xs transition-colors cursor-pointer text-left ${colors[type]} disabled:opacity-60`}
    >
      <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${dots[type]}`} />
      <span className="flex-1">{text}</span>
      <span className="text-2xs opacity-60 whitespace-nowrap flex items-center gap-1">
        {time}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-50">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </span>
    </button>
  );
}

function formatDate(v) {
  if (!v) return '—';
  if (typeof v === 'string') {
    const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const y = parseInt(m[1], 10);
      const mo = parseInt(m[2], 10) - 1;
      const d = parseInt(m[3], 10);
      return new Date(y, mo, d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    }
  }
  if (v instanceof Date && !isNaN(v.getTime())) {
    return new Date(v.getFullYear(), v.getMonth(), v.getDate()).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(v) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d)) return '';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCellValue(val, col) {
  if (val === null || val === undefined) return '—';
  if (col.key.includes('date') || col.key === 'created_at') return formatDate(val);
  if (col.align === 'right' && typeof val === 'number') return val.toLocaleString('en-IN');
  return String(val);
}

function AlertDetailModal({ data, onClose, navigate }) {
  const { title, columns = [], rows = [], linkBase, linkIdField, directLink, summary } = data;

  const handleRowClick = (row) => {
    if (directLink) { navigate(directLink); onClose(); return; }
    if (linkBase && linkIdField && row[linkIdField]) {
      navigate(`${linkBase}/${row[linkIdField]}`);
      onClose();
    } else if (linkBase && data.linkField && row[data.linkField]) {
      navigate(`${linkBase}/${encodeURIComponent(row[data.linkField])}`);
      onClose();
    }
  };

  const canClickRow = !!(linkBase || directLink);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-[95vw] max-w-[1000px] max-h-[85vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-sap-border bg-gray-50">
          <div className="flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <h2 className="text-sm font-bold text-sap-text">{title}</h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-2xs text-sap-textLight font-medium">
              {rows.length} record{rows.length !== 1 ? 's' : ''}
            </span>
            <button onClick={onClose} className="p-1 rounded-lg hover:bg-sap-border/40 transition-colors">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Summary (for single-record alerts like recentPO) */}
        {summary && (
          <div className="px-5 py-3 bg-gray-50 border-b border-sap-border/40 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            {Object.entries(summary).filter(([k]) => !k.endsWith('_id')).map(([k, v]) => (
              <div key={k}>
                <span className="text-sap-textLight text-2xs uppercase tracking-wider">{k.replace(/_/g, ' ')}</span>
                <p className="font-semibold text-sap-text mt-0.5">{k.includes('date') ? formatDate(v) : (v || '—')}</p>
              </div>
            ))}
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {columns.length === 0 || rows.length === 0 ? (
            <div className="py-12 text-center text-sap-textLight text-xs">No records found.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10">
                <tr className="bg-sap-blue/5 border-b border-sap-border/60">
                  <th className="text-left px-3 py-2.5 font-semibold text-2xs uppercase tracking-wider text-sap-textLight w-8">#</th>
                  {columns.map(col => (
                    <th
                      key={col.key}
                      className={`px-3 py-2.5 font-semibold text-2xs uppercase tracking-wider text-sap-textLight ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr
                    key={ri}
                    onClick={canClickRow ? () => handleRowClick(row) : undefined}
                    className={`border-t border-sap-border/30 transition-colors
                      ${ri % 2 === 1 ? 'bg-[#F0F4F3]' : ''}
                      ${canClickRow ? 'hover:bg-sap-blueLight/60 cursor-pointer' : 'hover:bg-sap-blueLight/30'}`}
                  >
                    <td className="px-3 py-1.5 text-sap-textLight">{ri + 1}</td>
                    {columns.map(col => (
                      <td
                        key={col.key}
                        className={`px-3 py-1.5 ${col.align === 'right' ? 'text-right font-medium' : ''} ${col.key === 'days_overdue' && Number(row[col.key]) > 7 ? 'text-red-600 font-bold' : ''} ${col.key === 'deficit' ? 'text-red-600 font-semibold' : ''}`}
                      >
                        {formatCellValue(row[col.key], col)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-sap-border/40 bg-gray-50">
          <span className="text-2xs text-sap-textLight">
            {canClickRow ? 'Click any row to open details' : ''}
          </span>
          <button onClick={onClose} className="px-4 py-1.5 text-xs bg-sap-blue text-white rounded-lg hover:bg-sap-blueDark transition-colors font-semibold">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function StockRow({ no, desc, inStock, committed, ordered, available, status }) {
  return (
    <tr className="border-t border-sap-border/40 hover:bg-sap-blueLight/40 transition-colors even:bg-[#F0F4F3]">
      <td className="px-3 py-1.5 font-semibold text-sap-blue border-l-2 border-sap-blue/60">{no}</td>
      <td className="px-3 py-1.5">{desc}</td>
      <td className="px-3 py-1.5 text-right">{inStock}</td>
      <td className="px-3 py-1.5 text-right">{committed}</td>
      <td className="px-3 py-1.5 text-right">{ordered}</td>
      <td className="px-3 py-1.5 text-right font-semibold">{available}</td>
      <td className="px-3 py-1.5 text-center">
        <span className={`inline-block px-2 py-0.5 rounded-full text-2xs font-semibold
          ${status === 'ok' ? 'bg-[#EDF5F0] text-[#4A9E6E]' : 'bg-[#FDF0EE] text-[#C4574B]'}`}>
          {status === 'ok' ? 'OK' : 'LOW'}
        </span>
      </td>
    </tr>
  );
}
